import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Clock, Heart } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import {
  scopeForUser,
  useMaintenanceStore,
} from '../stores/maintenanceStore'
import { landingFor } from '../lib/landing'
import { usePlatformBranding } from '../lib/branding'
import { formatDateTime, getDisplayTimeZone } from '../lib/format'

/**
 * Full-page "we're down for maintenance" screen.
 *
 * Renders for two audiences:
 *   - Signed-out visitors hitting the public site while the `website`
 *     scope is in maintenance.
 *   - Signed-in users whose panel scope (`company` / `user`) is in
 *     maintenance. They're routed here from their layout shell.
 *
 * Watches the maintenance store and auto-redirects back to the user's
 * landing page the instant the toggle is flipped off, so the user
 * doesn't have to refresh.
 *
 * Layout: two-column on desktop (text left, illustration right),
 * stacked single-column on mobile. The illustration is loaded from
 * `/maintenance.png` in the public folder — drop a 1024×800-ish PNG
 * there to swap the visual. The image gracefully degrades to "no
 * image rendered" if the file is missing, so the screen still works
 * in environments where the asset hasn't been provisioned.
 */
export function MaintenanceScreen() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const state = useMaintenanceStore((s) => s.state)
  // Same platform support address the footer and every email use.
  const { data: branding } = usePlatformBranding()
  const supportEmail = branding?.supportEmail || ''
  // Guards the one-time maintenance warning toast so it fires once per
  // visit, not on every store-driven re-render.
  const warnedRef = useRef(false)

  const scope = scopeForUser(user)
  const cfg = scope ? state[scope] : null

  useEffect(() => {
    // Super Admin doesn't have a scope — never holds them here.
    if (!scope) {
      navigate(landingFor(user?.role ?? 'user'), { replace: true })
      return
    }
    // Toggle was flipped off elsewhere — escape.
    if (cfg && !cfg.enabled) {
      navigate(user ? landingFor(user.role) : '/sign-in', { replace: true })
      return
    }
    // Genuinely down for this user — surface a one-time warning toast so it's
    // clear WHY the app is unavailable. Without it the only post-login
    // feedback is the green "Signed in successfully." toast, which reads as a
    // contradiction sitting next to the maintenance screen.
    if (!warnedRef.current) {
      warnedRef.current = true
      toast.warning(
        'The system is currently under maintenance. Please check back shortly.',
      )
    }
  }, [scope, cfg, navigate, user])

  const message =
    cfg?.message ||
    "We're currently undergoing scheduled maintenance to improve your experience. Thank you for your patience!"

  // "Expected back" chip — only render when an end time is set AND
  // it's actually in the future. A past end time would read as a lie
  // ("expected back at 9am" when it's 11am) so we hide the chip in
  // that case and let the generic message stand alone.
  const expectedBackAt = (() => {
    if (!cfg?.scheduledEnd) return null
    const d = new Date(cfg.scheduledEnd)
    if (Number.isNaN(d.getTime())) return null
    if (d.getTime() <= new Date().getTime()) return null
    return d
  })()

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] flex flex-col">
      {/* Body — two columns on lg+, stacked otherwise. Wider cap
       *  (max-w-7xl) and roomier vertical padding so the headline
       *  has air to breathe and the section fills more of the
       *  viewport, instead of feeling stranded in the middle. */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column — text. Wider max so the one-line headline
           *  "We'll be right back!" has room to render without
           *  wrapping. */}
          <div className="max-w-2xl">
            {/* `whitespace-nowrap` enforces the single-line render
             *  even at narrower viewport widths — the responsive
             *  font sizing (text-5xl → text-7xl) keeps it visually
             *  big without pushing past the column edge on tablet. */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[color:var(--color-ink-strong)] leading-[1.05] tracking-tight whitespace-nowrap">
              We&rsquo;ll be right back!
            </h1>
            {/* Body copy bumped from text-base → text-lg so it
             *  pairs better with the larger headline above. Same
             *  one-step-up treatment applied to every other text
             *  block on this screen for consistency. */}
            <p className="mt-6 text-lg text-[color:var(--color-ink-muted)] leading-relaxed max-w-lg">
              {message}
            </p>

            {expectedBackAt ? (
              <div className="mt-8 inline-flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-accent-bg)] px-5 py-4 max-w-sm">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--color-card)] text-[color:var(--color-accent)] shrink-0">
                  <Clock size={20} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-[color:var(--color-ink-muted)]">
                    Expected to be back at
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-[color:var(--color-ink-strong)] tabular-nums">
                    {formatExpected(expectedBackAt)}
                  </p>
                </div>
              </div>
            ) : null}

            <p className="mt-10 inline-flex items-center gap-2 text-base text-[color:var(--color-ink-muted)]">
              <Heart
                size={18}
                className="text-[color:var(--color-accent)]"
                fill="currentColor"
              />
              We appreciate your understanding.
            </p>

            {/* Somewhere to go for anything that cannot wait for the
              *  window to close. Omitted rather than faked when no support
              *  address is configured (QA, 2026-09-01). */}
            {supportEmail ? (
              <p className="mt-6 text-base text-[color:var(--color-ink-muted)]">
                Need urgent help?{' '}
                <a
                  href={`mailto:${supportEmail}`}
                  className="font-semibold text-[color:var(--color-accent-strong)] underline underline-offset-2 hover:no-underline"
                >
                  Email {supportEmail}
                </a>
                .
              </p>
            ) : null}

            <p className="mt-6 text-sm text-[color:var(--color-ink-subtle)]">
              This page will refresh automatically as soon as service is
              restored.
            </p>
          </div>

          {/* Right column — illustration. Drop the PNG at
           *  `public/maintenance.png` (the project's existing
           *  static-asset convention) to swap visuals. `onError`
           *  hides the broken-image icon if the asset is missing
           *  so a misconfigured deploy degrades to a clean text-
           *  only screen rather than a chrome 404 thumbnail.
           *
           *  `max-w-2xl` (672px) + `max-h-[640px]` gives the
           *  illustration real visual presence — it now reads as
           *  the primary subject of the right column instead of a
           *  modest accent next to the text. `object-contain`
           *  keeps the aspect ratio intact whatever the source PNG
           *  dimensions happen to be. */}
          <div className="hidden lg:flex items-center justify-center">
            <img
              src="/maintenance.png"
              alt="Under maintenance"
              className="w-full max-w-2xl max-h-[640px] h-auto object-contain select-none"
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Render the expected-back time using the canonical date + time
 *  formatter (the person's formats). The zone suffix keeps the line
 *  meaningful for users reading from other timezones — they know which
 *  clock the value is anchored to. */
function formatExpected(d) {
  // formatDateTime renders in the person's zone (else the browser's), so
  // label THAT zone — a fixed "(UTC)" mislabelled the wall-clock.
  const zone = getDisplayTimeZone() || Intl.DateTimeFormat().resolvedOptions().timeZone
  return zone ? `${formatDateTime(d)} (${zone})` : formatDateTime(d)
}
